// Characters screen: browse every saved ★ character. Expandable cards show
// personality + traits; Delete forgets them permanently. The creator makes
// hand-made (human origin) characters.
import { useState } from 'react';
import { addCustom, loadLibrary, removeSaved, type LibraryEntry } from './library.js';
import CharacterCreator from './CharacterCreator.js';
import type { Npc } from '../types.js';

function Card({ entry, onDelete }: { entry: LibraryEntry; onDelete: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const n = entry.npc;
  const badge = n.origin === 'llm' ? '✦ LLM' : n.origin === 'human' ? '◈ human' : '○ offline';
  const badgeCls = n.origin === 'llm' ? 'origin-llm' : n.origin === 'human' ? 'origin-human' : 'origin-offline';
  return (
    <div className="char-card">
      <button className="char-head" onClick={() => setOpen((o) => !o)}>
        <span>
          <strong>{n.firstName} {n.lastName}</strong>{' '}
          <span className={badgeCls}>
            {badge}
          </span>{' '}
          {n.neurotic && <span className="neg">intense</span>}
        </span>
        <span className="mut">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="char-body">
          <div className="mut">
            {Object.entries(n.nature).map(([k, v]) => `${k} ${v}`).join(' · ')}
          </div>
          {n.nurture.map((t) => (
            <div key={t.trait_key} className="trait">
              <strong>{t.trait_key}</strong> ({t.strength}/10)
              <div className="mut">{t.reason}</div>
            </div>
          ))}
          <button className="btn-ghost" onClick={onDelete}>Forget</button>
        </div>
      )}
    </div>
  );
}

export default function CharactersScreen(props: { onBack: () => void }): JSX.Element {
  const [entries, setEntries] = useState<LibraryEntry[]>(() => loadLibrary());
  const [creating, setCreating] = useState(false);
  const created = (npc: Npc): void => {
    const next = addCustom(npc);
    if (next) setEntries(next);
    setCreating(false);
  };
  return (
    <div className="screen screen-wide">
      <h1>Saved Characters</h1>
      <div className="subtitle">
        {entries.length === 0
          ? 'No one saved yet — ★ a character from their radial menu, or make your own.'
          : `${entries.length} saved. Pick them into your cast when starting a world.`}
      </div>
      <div className="menu-row">
        <button className="btn-primary" onClick={() => setCreating(true)}>+ Create Character</button>
      </div>
      {entries.map((e) => (
        <Card key={e.npc.id} entry={e} onDelete={() => {
          const name = `${e.npc.firstName} ${e.npc.lastName}`.trim();
          if (!window.confirm(`Forget "${name}"? This cannot be undone.`)) return;
          setEntries(removeSaved(e.npc.id));
        }} />
      ))}
      <div className="menu-row">
        <button className="btn-ghost" onClick={props.onBack}>Back</button>
      </div>
      {creating && <CharacterCreator onSave={created} onClose={() => setCreating(false)} />}
    </div>
  );
}
